import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";

/**
 * Searchable Star Citizen item picker — used by event loadout editors and any
 * future "select from catalog" flow (shopping lists, etc.).
 *
 * Props:
 *   open           — show/hide
 *   onClose        — called when user dismisses
 *   onSelect(item) — called when user picks; receives full sc_items row
 *   groupFilter    — optional array of item_group names to limit picker to (e.g. ["Components","Ship Weapons"])
 *   categoryFilter — optional array of category names to further narrow (e.g. ["Weapon","Armor"])
 *   title          — header text (defaults to "Add Item")
 *   accentColor    — accent color for styling (defaults to gold)
 *
 * Internally lazy-loads sc_items once on first open and caches in-memory for the
 * session so opening the picker repeatedly inside an event editor is instant.
 */

let _itemsCache = null;
let _loadingPromise = null;

async function loadItems() {
  if (_itemsCache) return _itemsCache;
  if (_loadingPromise) return _loadingPromise;
  _loadingPromise = (async () => {
    // Supabase caps a single .select() at 1000 rows — sc_items is ~5k. Page
    // through with .range() until we get a short page.
    const pageSize = 1000;
    const out = [];
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await supabase
        .from("sc_items")
        .select("id, name, item_group, category, subcategory, manufacturer, image_url, description, source_url, tags")
        .eq("archived", false)
        .order("name")
        .range(from, from + pageSize - 1);
      if (error) { console.error("[ItemPicker] page load failed", error); break; }
      if (!data || data.length === 0) break;
      out.push(...data);
      if (data.length < pageSize) break;
    }
    _itemsCache = out;
    _loadingPromise = null;
    return _itemsCache;
  })();
  return _loadingPromise;
}

export default function ItemPicker({
  open,
  onClose,
  onSelect,
  groupFilter = null,
  categoryFilter = null,
  title = "Add Item",
  accentColor = "#fcd657",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("");
  const [activeCategory, setActiveCategory] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadItems().then(data => {
      if (cancelled) return;
      setItems(data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  // Reset state when opening so the picker doesn't remember last-time's filters.
  useEffect(() => {
    if (open) {
      setSearch("");
      setActiveGroup("");
      setActiveCategory("");
    }
  }, [open]);

  // Apply outer prop filters first (groupFilter / categoryFilter), then inner UI filters.
  const baseFiltered = useMemo(() => {
    return items.filter(i => {
      if (groupFilter && groupFilter.length && !groupFilter.includes(i.item_group)) return false;
      if (categoryFilter && categoryFilter.length && !categoryFilter.includes(i.category) && !(Array.isArray(i.tags) && i.tags.some(t => categoryFilter.includes(t)))) return false;
      return true;
    });
  }, [items, groupFilter, categoryFilter]);

  const groups = useMemo(() => [...new Set(baseFiltered.map(i => i.item_group).filter(Boolean))].sort(), [baseFiltered]);
  const categories = useMemo(() => {
    const pool = activeGroup ? baseFiltered.filter(i => i.item_group === activeGroup) : baseFiltered;
    const cats = new Set();
    pool.forEach(i => {
      if (i.category) cats.add(i.category);
      if (Array.isArray(i.tags)) i.tags.forEach(t => cats.add(t));
    });
    return [...cats].sort();
  }, [baseFiltered, activeGroup]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseFiltered.filter(i => {
      if (activeGroup && i.item_group !== activeGroup) return false;
      if (activeCategory && i.category !== activeCategory && !(Array.isArray(i.tags) && i.tags.includes(activeCategory))) return false;
      if (q) {
        const hay = `${i.name || ""} ${i.manufacturer || ""} ${i.category || ""} ${i.subcategory || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [baseFiltered, search, activeGroup, activeCategory]);

  if (!open) return null;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
      zIndex: 11000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0a0610", border: `1px solid ${accentColor}66`,
        boxShadow: `0 0 40px ${accentColor}25, inset 0 0 40px rgba(0,0,0,0.6)`,
        maxWidth: 720, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column",
        clipPath: "polygon(14px 0%, 100% 0%, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0% 100%, 0% 14px)",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${accentColor}22`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: accentColor, letterSpacing: 2, textTransform: "uppercase" }}>{title}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, padding: "6px 12px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
        </div>

        {/* Filters */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="text" placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)}
            autoFocus
            style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${accentColor}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2 }}
          />
          {(!groupFilter || groups.length > 1) && (
            <select value={activeGroup} onChange={e => { setActiveGroup(e.target.value); setActiveCategory(""); }}
              style={{ padding: "8px 12px", background: "#05070f", border: `1px solid ${accentColor}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, borderRadius: 2, minWidth: 140 }}>
              <option value="">All Groups</option>
              {groups.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          )}
          <select value={activeCategory} onChange={e => setActiveCategory(e.target.value)}
            style={{ padding: "8px 12px", background: "#05070f", border: `1px solid ${accentColor}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, borderRadius: 2, minWidth: 160 }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Results */}
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>Loading catalog…</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>No items match</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {visible.slice(0, 200).map(item => (
                <button key={item.id} onClick={() => onSelect(item)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", textAlign: "left", background: "transparent", border: "none", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${accentColor}10`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  <div style={{ width: 40, height: 40, flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 14, color: `${accentColor}25` }}>◇</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.category}{item.subcategory ? ` · ${item.subcategory}` : ""}{item.manufacturer ? ` · ${item.manufacturer}` : ""}
                    </div>
                  </div>
                </button>
              ))}
              {visible.length > 200 && (
                <div style={{ padding: "12px 20px", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1, textAlign: "center", textTransform: "uppercase", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                  +{visible.length - 200} more — narrow your search to see them
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
